import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSeasonLog } from "../components/tier/useSeasonLog";
import { parseSpecterScrimP } from "../utils/excelParser";
import heroList from "../data/heroList.json";
import { TEAM_SHORT } from "../theme/brandTheme";
import {
  canonicalHeroName,
  getHeroImageUrl as resolveHeroImageUrl,
  normalizeHeroKey,
} from "../utils/heroCatalog";

const ROW_COLUMNS = [
  "date", "week", "gameNo", "gameType",
  "bruBan1", "bruBan2", "bruBan3", "bruBan4",
  "bruDSL", "bruJUG", "bruMID", "bruSUP", "bruADL",
  "bruWin",
  "opDSL", "opJUG", "opMID", "opSUP", "opADL",
  "opBan1", "opBan2", "qi", "biron", "ref",
];

const HERO_COLUMNS = [
  "bruBan1", "bruBan2", "bruBan3", "bruBan4",
  "bruDSL", "bruJUG", "bruMID", "bruSUP", "bruADL",
  "opDSL", "opJUG", "opMID", "opSUP", "opADL",
  "opBan1", "opBan2", "qi", "biron",
];

const COLUMN_WIDTHS = {
  date: 118,
  week: 64,
  gameNo: 64,
  gameType: 86,
  bruBan1: 110,
  bruBan2: 110,
  bruBan3: 110,
  bruBan4: 110,
  bruDSL: 116,
  bruJUG: 116,
  bruMID: 116,
  bruSUP: 116,
  bruADL: 116,
  bruWin: 92,
  opDSL: 116,
  opJUG: 116,
  opMID: 116,
  opSUP: 116,
  opADL: 116,
  opBan1: 110,
  opBan2: 110,
  qi: 110,
  biron: 110,
  ref: 92,
};

const EXPORT_HEADERS = {
  date: "Date",
  week: "Week",
  gameNo: "Game",
  gameType: "Type",
  bruBan1: "TEAM BAN 1",
  bruBan2: "TEAM BAN 2",
  bruBan3: "TEAM BAN 3",
  bruBan4: "TEAM BAN 4",
  bruDSL: "TEAM DSL",
  bruJUG: "TEAM JUG",
  bruMID: "TEAM MID",
  bruSUP: "TEAM SUP",
  bruADL: "TEAM ADL",
  bruWin: "TEAM WIN",
  opDSL: "OPP DSL",
  opJUG: "OPP JUG",
  opMID: "OPP MID",
  opSUP: "OPP SUP",
  opADL: "OPP ADL",
  opBan1: "OPP BAN 1",
  opBan2: "OPP BAN 2",
  qi: "OPP BAN 3",
  biron: "OPP BAN 4",
  ref: "#REF!",
};

const recapTheme = {
  bg: "#090909",
  bgDark: "#050505",
  panel: "rgba(12,12,12,.96)",
  panelSoft: "rgba(255,255,255,.03)",
  border: "rgba(255,16,38,.28)",
  borderSoft: "rgba(255,255,255,.08)",
  borderStrong: "rgba(255,140,51,.42)",
  text: "#ffffff",
  textDim: "#b3b3b3",
  textMuted: "#6e6e6e",
  primary: "#ff1026",
  primaryLight: "#ff934d",
  primaryGlow: "rgba(255,16,38,.24)",
  success: "#00e08f",
  successSoft: "rgba(0,224,143,.18)",
  successStrong: "rgba(0,224,143,.28)",
  danger: "#ff5577",
  dangerSoft: "rgba(255,85,119,.16)",
  dangerStrong: "rgba(255,85,119,.28)",
  team: "#ff7a4d",
  teamSoft: "rgba(255,122,77,.16)",
  opp: "#7da3ff",
  oppSoft: "rgba(125,163,255,.16)",
  shadow: "0 14px 34px rgba(0,0,0,.38), 0 0 16px rgba(255,16,38,.10)",
  dividerLine: "#ffffff",
};

const COLUMN_GROUPS = [
  { label: "META", color: "rgba(255,255,255,.55)", span: 4, accent: "rgba(255,255,255,.10)" },
  { label: "TEAM BANS", color: "#ff9c7a", span: 4, accent: "rgba(255,122,77,.22)" },
  { label: "TEAM PICKS", color: "#ff9c7a", span: 5, accent: "rgba(255,122,77,.30)" },
  { label: "RESULT", color: "#7df5b8", span: 1, accent: "rgba(0,224,143,.32)" },
  { label: "OPP PICKS", color: "#a7c0ff", span: 5, accent: "rgba(125,163,255,.30)" },
  { label: "OPP BANS", color: "#a7c0ff", span: 4, accent: "rgba(125,163,255,.22)" },
  { label: "REF", color: "rgba(255,255,255,.55)", span: 1, accent: "rgba(255,255,255,.10)" },
];

const COLUMN_COLORS = {
  date: { head: "rgba(255,255,255,.10)", body: "rgba(255,255,255,.028)" },
  week: { head: "rgba(255,255,255,.10)", body: "rgba(255,255,255,.028)" },
  gameNo: { head: "rgba(255,255,255,.10)", body: "rgba(255,255,255,.028)" },
  gameType: { head: "rgba(255,255,255,.10)", body: "rgba(255,255,255,.028)" },
  bruBan1: { head: "rgba(66,66,66,.88)", body: "rgba(28,28,28,.72)" },
  bruBan2: { head: "rgba(66,66,66,.88)", body: "rgba(28,28,28,.72)" },
  bruBan3: { head: "rgba(66,66,66,.88)", body: "rgba(28,28,28,.72)" },
  bruBan4: { head: "rgba(66,66,66,.88)", body: "rgba(28,28,28,.72)" },
  bruDSL: { head: "rgba(120,84,84,.82)", body: "rgba(78,33,39,.70)" },
  bruJUG: { head: "rgba(120,84,84,.82)", body: "rgba(78,33,39,.70)" },
  bruMID: { head: "rgba(120,84,84,.82)", body: "rgba(78,33,39,.70)" },
  bruADL: { head: "rgba(120,84,84,.82)", body: "rgba(78,33,39,.70)" },
  bruSUP: { head: "rgba(120,84,84,.82)", body: "rgba(78,33,39,.70)" },
  bruWin: { head: "rgba(23,86,46,.92)", body: "rgba(17,70,38,.76)" },
  opDSL: { head: "rgba(74,80,92,.92)", body: "rgba(39,44,58,.78)" },
  opJUG: { head: "rgba(74,80,92,.92)", body: "rgba(39,44,58,.78)" },
  opMID: { head: "rgba(74,80,92,.92)", body: "rgba(39,44,58,.78)" },
  opADL: { head: "rgba(74,80,92,.92)", body: "rgba(39,44,58,.78)" },
  opSUP: { head: "rgba(74,80,92,.92)", body: "rgba(39,44,58,.78)" },
  opBan1: { head: "rgba(74,54,36,.92)", body: "rgba(74,33,39,.70)" },
  opBan2: { head: "rgba(74,54,36,.92)", body: "rgba(74,33,39,.70)" },
  qi: { head: "rgba(74,54,36,.92)", body: "rgba(74,33,39,.70)" },
  biron: { head: "rgba(74,54,36,.92)", body: "rgba(74,33,39,.70)" },
  ref: { head: "rgba(92,70,44,.92)", body: "rgba(72,55,35,.72)" },
};

function getColumnColors(field) {
  return COLUMN_COLORS[field] || { head: "rgba(255,255,255,.06)", body: "rgba(255,255,255,.02)" };
}

const heroCanonicalMap = new Map(
  (Array.isArray(heroList) ? heroList : []).map((hero) => [normalizeHeroKey(hero?.name), String(hero?.name || "").trim()])
);
const heroCanonicalKeys = Array.from(heroCanonicalMap.keys());

function damerauLevenshtein(a = "", b = "") {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;

  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) dp[i][0] = i;
  for (let j = 0; j <= n; j += 1) dp[0][j] = j;

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );

      if (
        i > 1 &&
        j > 1 &&
        a[i - 1] === b[j - 2] &&
        a[i - 2] === b[j - 1]
      ) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1);
      }
    }
  }

  return dp[m][n];
}

function sharedPrefixLength(a = "", b = "") {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return i;
}

function correctHeroSpelling(value = "") {
  const raw = String(value || "").trim();
  const canonical = canonicalHeroName(raw);
  if (canonical && normalizeHeroKey(canonical) !== normalizeHeroKey(raw)) return canonical;

  const key = normalizeHeroKey(raw);
  if (!key) return "";
  if (heroCanonicalMap.has(key)) return heroCanonicalMap.get(key) || raw;
  if (key.length < 4) return raw;

  let bestKey = "";
  let bestDistance = Infinity;
  let secondDistance = Infinity;

  heroCanonicalKeys.forEach((candidate) => {
    const distance = damerauLevenshtein(key, candidate);
    if (distance < bestDistance) {
      secondDistance = bestDistance;
      bestDistance = distance;
      bestKey = candidate;
    } else if (distance < secondDistance) {
      secondDistance = distance;
    }
  });

  if (!bestKey) return raw;

  const longest = Math.max(key.length, bestKey.length);
  const similarity = 1 - (bestDistance / longest);
  const secondSimilarity = Number.isFinite(secondDistance) ? 1 - (secondDistance / longest) : 0;
  const prefix = sharedPrefixLength(key, bestKey);
  const firstLetterMatch = key[0] === bestKey[0];
  const lastLetterMatch = key[key.length - 1] === bestKey[bestKey.length - 1];
  const maxDistance = key.length >= 8 ? 3 : 2;
  const clearWinner = similarity - secondSimilarity >= 0.06;
  const acceptable =
    firstLetterMatch &&
    bestDistance <= maxDistance &&
    similarity >= 0.72 &&
    (prefix >= 4 || lastLetterMatch || similarity >= 0.86) &&
    clearWinner;

  return acceptable ? (heroCanonicalMap.get(bestKey) || raw) : raw;
}

function normalizeHeroValue(value = "") {
  return correctHeroSpelling(value);
}

function isLikelyWinValue(value = "") {
  return String(value).trim() === "0" || String(value).trim() === "1";
}

function isLikelyRefValue(value = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  const upper = text.toUpperCase();
  if (!/^[A-Z0-9!#]{2,8}$/.test(upper)) return false;
  return !heroCanonicalMap.has(normalizeHeroKey(text));
}

function isLikelyHeroText(value = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  if (isLikelyWinValue(text) || isLikelyRefValue(text)) return false;
  const normalized = normalizeHeroKey(normalizeHeroValue(text));
  if (heroCanonicalMap.has(normalized)) return true;
  return /[a-z]/i.test(text);
}

function getHeroImageUrl(heroName = "") {
  return resolveHeroImageUrl(normalizeHeroValue(heroName)) || "";
}

function WinCell({ value = 0 }) {
  const won = Number(value) === 1;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "5px 10px",
        borderRadius: 999,
        fontWeight: 900,
        fontSize: 12,
        letterSpacing: ".06em",
        background: won ? recapTheme.successStrong : recapTheme.dangerStrong,
        color: won ? "#a8ffd5" : "#ffc4cf",
        border: `1px solid ${won ? "rgba(0,224,143,.45)" : "rgba(255,85,119,.45)"}`,
        boxShadow: won
          ? "0 0 12px rgba(0,224,143,.20), inset 0 0 8px rgba(0,224,143,.12)"
          : "0 0 12px rgba(255,85,119,.18), inset 0 0 8px rgba(255,85,119,.10)",
        minWidth: 54,
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1 }}>{won ? "✓" : "✗"}</span>
      <span>{won ? "WIN" : "LOSS"}</span>
    </div>
  );
}

function HeroCell({ value = "" }) {
  const text = normalizeHeroValue(value);
  const imageUrl = getHeroImageUrl(text);

  if (!text) {
    return <span style={{ display: "inline-block", minHeight: 16 }} aria-hidden="true" />;
  }

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 4, maxWidth: "100%" }}>
      <div
        title={text}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          overflow: "hidden",
          border: `1px solid ${recapTheme.borderSoft}`,
          background: "rgba(255,255,255,.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 18px rgba(0,0,0,.25)",
        }}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={text} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ fontSize: 10, fontWeight: 900, color: recapTheme.textDim }}>
            {text.slice(0, 2).toUpperCase()}
          </div>
        )}
      </div>
      <span
        title={text}
        style={{
          maxWidth: 78,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          whiteSpace: "normal",
          fontSize: 9.5,
          lineHeight: 1.1,
          color: recapTheme.textDim,
          fontWeight: 700,
          textAlign: "center",
          minHeight: 20,
        }}
      >
        {text}
      </span>
    </div>
  );
}

function renderHeaderLabel(field) {
  const label = EXPORT_HEADERS[field] || field;
  if (field === "ref") return "#REF!";

  const match = label.match(/^(.*? BAN) (\d)$/);
  if (match) {
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.05 }}>
        <span>{match[1]}</span>
        <span>{match[2]}</span>
      </span>
    );
  }

  return label;
}

function ymd(raw = "") {
  return String(raw || "").replace(/[^\d]/g, "").slice(0, 8);
}

export function normalizeDraftImageDate(raw = "") {
  const digits = ymd(raw);
  const ocrMarchDay = Number(digits.slice(6, 8));
  if (/^202006[0-3]\d$/.test(digits) && ocrMarchDay >= 25 && ocrMarchDay <= 31) {
    return `202603${digits.slice(6, 8)}`;
  }
  return digits;
}

function weekNum(value = "") {
  const match = String(value || "").match(/(\d+)/);
  return match ? match[1] : "";
}

function normalizePos(pos = "") {
  const token = String(pos || "").trim().toUpperCase();
  if (token === "JGL" || token === "JUNGLE") return "JUG";
  if (token === "MIDLANE") return "MID";
  if (token === "ROAMING" || token === "SUPPORT") return "SUP";
  return token;
}

function pickByPos(picks = [], positions = [], target = "") {
  const normalizedTarget = normalizePos(target);
  const idx = (positions || []).findIndex((pos) => normalizePos(pos) === normalizedTarget);
  if (idx < 0) return "";
  return String(picks[idx] || "").trim();
}

function pickRoleValue(game = {}, sideKey = "fsByRole", picksKey = "fsPick", positionsKey = "fsPosition", role = "") {
  const normalizedRole = normalizePos(role);
  const directValue = String(game?.[sideKey]?.[normalizedRole] || "").trim();
  if (directValue) return directValue;
  return pickByPos(game?.[picksKey], game?.[positionsKey], normalizedRole);
}

function getSeriesKey(row = {}) {
  const date = String(row?.date || "").trim();
  const ref = String(row?.ref || "").trim();
  if (date && ref) return `${date}|${ref}`;
  if (date) return date;
  if (ref) return ref;
  return "";
}

function isSeriesBoundary(currentRow = {}, nextRow = null) {
  if (!nextRow) return true;
  const currentSeriesKey = getSeriesKey(currentRow);
  const nextSeriesKey = getSeriesKey(nextRow);
  if (currentSeriesKey && nextSeriesKey && currentSeriesKey !== nextSeriesKey) return true;

  const currentGameNo = Number(currentRow?.gameNo || 0);
  const nextGameNo = Number(nextRow?.gameNo || 0);
  if (currentGameNo > 0 && nextGameNo > 0 && nextGameNo <= currentGameNo) return true;

  const currentDate = String(currentRow?.date || "").trim();
  const nextDate = String(nextRow?.date || "").trim();
  return Boolean(currentDate && nextDate && currentDate !== nextDate);
}

function createEmptyRow(idx = 0) {
  return {
    date: "",
    week: "",
    gameNo: idx + 1,
    gameType: "",
    bruBan1: "",
    bruBan2: "",
    bruBan3: "",
    bruBan4: "",
    bruDSL: "",
    bruJUG: "",
    bruMID: "",
    bruADL: "",
    bruSUP: "",
    bruWin: 0,
    opDSL: "",
    opJUG: "",
    opMID: "",
    opADL: "",
    opSUP: "",
    opBan1: "",
    opBan2: "",
    qi: "",
    biron: "",
    ref: "",
    _id: `image-row-${idx}-${Date.now()}`,
    _source: "image",
  };
}

function normalizeImageRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    date: normalizeDraftImageDate(row?.date),
    week: String(row?.week || "").replace(/[^\d]/g, ""),
    gameNo: Number(String(row?.gameNo || "").replace(/[^\d]/g, "")) || idx + 1,
    gameType: String(row?.gameType || "").trim(),
    bruBan1: normalizeHeroValue(row?.bruBan1),
    bruBan2: normalizeHeroValue(row?.bruBan2),
    bruBan3: normalizeHeroValue(row?.bruBan3),
    bruBan4: normalizeHeroValue(row?.bruBan4),
    bruDSL: normalizeHeroValue(row?.bruDSL),
    bruJUG: normalizeHeroValue(row?.bruJUG),
    bruMID: normalizeHeroValue(row?.bruMID),
    bruSUP: normalizeHeroValue(row?.bruSUP),
    bruADL: normalizeHeroValue(row?.bruADL),
    bruWin: Number(row?.bruWin) === 1 ? 1 : 0,
    opDSL: normalizeHeroValue(row?.opDSL),
    opJUG: normalizeHeroValue(row?.opJUG),
    opMID: normalizeHeroValue(row?.opMID),
    opSUP: normalizeHeroValue(row?.opSUP),
    opADL: normalizeHeroValue(row?.opADL),
    opBan1: normalizeHeroValue(row?.opBan1),
    opBan2: normalizeHeroValue(row?.opBan2),
    qi: normalizeHeroValue(row?.qi),
    biron: normalizeHeroValue(row?.biron),
    ref: String(row?.ref || "").trim(),
    _id: row?._id || `image-row-${idx}-${Date.now()}`,
    _source: "image",
  }));
}

function buildExportRows(rows = []) {
  return (rows || []).map((row) => {
    const out = {};
    ROW_COLUMNS.forEach((column) => {
      out[EXPORT_HEADERS[column] || column] = row?.[column] ?? "";
    });
    return out;
  });
}

const BRU_BLOCK_FIELDS = ["bruBan1", "bruBan2", "bruBan3", "bruBan4", "bruDSL", "bruJUG", "bruMID", "bruSUP", "bruADL"];
const OP_BLOCK_FIELDS = ["opDSL", "opJUG", "opMID", "opSUP", "opADL", "opBan1", "opBan2", "qi", "biron"];
const BRU_BAN_FIELDS = ["bruBan1", "bruBan2", "bruBan3", "bruBan4"];
const OP_BAN_FIELDS = ["opBan1", "opBan2", "qi", "biron"];
const BAN_FIELDS = [...BRU_BAN_FIELDS, ...OP_BAN_FIELDS];
const SHIFTABLE_FIELDS = [...BRU_BLOCK_FIELDS, "bruWin", ...OP_BLOCK_FIELDS, "ref"];

function getRowAnchorKey(row = {}) {
  return [
    ymd(row?.date),
    String(row?.week || "").trim(),
    String(row?.gameNo || "").trim(),
    String(row?.ref || "").trim().toUpperCase(),
  ].join("|");
}

function normalizeRefKey(value = "") {
  return String(value || "").trim().toUpperCase().replace(/[^A-Z0-9!#]/g, "");
}

function normalizeGameType(value = "") {
  return String(value || "").trim().toLowerCase();
}

function heroFieldMatchScore(a = "", b = "") {
  const aKey = normalizeHeroKey(a);
  const bKey = normalizeHeroKey(b);
  if (!aKey || !bKey) return 0;
  return aKey === bKey ? 3 : 0;
}

function rowMatchScore(row = {}, template = {}) {
  let score = 0;

  const rowRef = normalizeRefKey(row?.ref);
  const templateRef = normalizeRefKey(template?.ref);
  if (rowRef && templateRef) score += rowRef === templateRef ? 8 : -5;

  const rowWin = String(row?.bruWin ?? "").trim();
  const templateWin = String(template?.bruWin ?? "").trim();
  if (isLikelyWinValue(rowWin) && isLikelyWinValue(templateWin)) {
    score += rowWin === templateWin ? 5 : -4;
  }

  const rowGameType = normalizeGameType(row?.gameType);
  const templateGameType = normalizeGameType(template?.gameType);
  if (rowGameType && templateGameType) score += rowGameType === templateGameType ? 2 : -1;

  const rowWeek = String(row?.week || "").trim();
  const templateWeek = String(template?.week || "").trim();
  if (rowWeek && templateWeek) score += rowWeek === templateWeek ? 2 : -1;

  const rowGameNo = Number(row?.gameNo || 0);
  const templateGameNo = Number(template?.gameNo || 0);
  if (rowGameNo > 0 && templateGameNo > 0) score += rowGameNo === templateGameNo ? 3 : -2;

  const rowDate = ymd(row?.date);
  const templateDate = ymd(template?.date);
  if (rowDate && templateDate) score += rowDate === templateDate ? 4 : -3;

  HERO_COLUMNS.forEach((field) => {
    score += heroFieldMatchScore(row?.[field], template?.[field]);
  });

  const rowHeroCount = HERO_COLUMNS.reduce((sum, field) => sum + (isLikelyHeroText(row?.[field]) ? 1 : 0), 0);
  const templateHeroCount = HERO_COLUMNS.reduce((sum, field) => sum + (String(template?.[field] || "").trim() ? 1 : 0), 0);
  if (rowHeroCount > 0 && templateHeroCount > 0) {
    score -= Math.abs(rowHeroCount - templateHeroCount) * 0.5;
  }

  return score;
}

function countAnchorMatches(row = {}, template = {}) {
  let matches = 0;

  const rowDate = ymd(row?.date);
  const templateDate = ymd(template?.date);
  if (rowDate && templateDate && rowDate === templateDate) matches += 1;

  const rowWeek = String(row?.week || "").trim();
  const templateWeek = String(template?.week || "").trim();
  if (rowWeek && templateWeek && rowWeek === templateWeek) matches += 1;

  const rowGameNo = Number(row?.gameNo || 0);
  const templateGameNo = Number(template?.gameNo || 0);
  if (rowGameNo > 0 && templateGameNo > 0 && rowGameNo === templateGameNo) matches += 1;

  const rowGameType = normalizeGameType(row?.gameType);
  const templateGameType = normalizeGameType(template?.gameType);
  if (rowGameType && templateGameType && rowGameType === templateGameType) matches += 1;

  const rowWin = String(row?.bruWin ?? "").trim();
  const templateWin = String(template?.bruWin ?? "").trim();
  if (isLikelyWinValue(rowWin) && isLikelyWinValue(templateWin) && rowWin === templateWin) matches += 1;

  const rowRef = normalizeRefKey(row?.ref);
  const templateRef = normalizeRefKey(template?.ref);
  if (rowRef && templateRef && rowRef === templateRef) matches += 1;

  return matches;
}

function hasTemplateLayoutDrift(row = {}, template = {}) {
  return [BRU_BLOCK_FIELDS, OP_BLOCK_FIELDS].some((fields) =>
    fields.some((field) => {
      const rowHasHero = isLikelyHeroText(row?.[field]);
      const templateHasValue = Boolean(String(template?.[field] || "").trim());
      return (rowHasHero && !templateHasValue) || (!rowHasHero && templateHasValue);
    })
  );
}

function shouldUseMatchedTemplate(row = {}, template = null, matchScore = 0) {
  if (!template) return false;
  if (hasStrongTemplateAnchor(row, template) && matchScore >= 4) return true;
  return false;
}

function shouldUseOrderedTemplate(row = {}, template = null, matchScore = 0) {
  if (!template || matchScore < 6) return false;
  return shouldUseIndexTemplate(row, template);
}

function shouldUseIndexTemplate(row = {}, template = null) {
  if (!template) return false;

  const rowGameNo = Number(row?.gameNo || 0);
  const templateGameNo = Number(template?.gameNo || 0);
  if (rowGameNo > 0 && templateGameNo > 0 && rowGameNo !== templateGameNo) return false;

  const rowGameType = normalizeGameType(row?.gameType);
  const templateGameType = normalizeGameType(template?.gameType);
  if (rowGameType && templateGameType && rowGameType !== templateGameType) return false;

  return true;
}

function rowIndexTemplateScore(row = {}, template = {}) {
  if (!template) return -Infinity;
  let score = 0;
  let signals = 0;

  const rowDate = ymd(row?.date);
  const templateDate = ymd(template?.date);
  if (rowDate && templateDate) {
    signals += 1;
    score += rowDate === templateDate ? 4 : -1;
  }

  const rowGameNo = Number(row?.gameNo || 0);
  const templateGameNo = Number(template?.gameNo || 0);
  if (rowGameNo > 0 && templateGameNo > 0) {
    signals += 1;
    score += rowGameNo === templateGameNo ? 5 : -5;
  }

  const rowWin = String(row?.bruWin ?? "").trim();
  const templateWin = String(template?.bruWin ?? "").trim();
  if (isLikelyWinValue(rowWin) && isLikelyWinValue(templateWin)) {
    signals += 1;
    score += rowWin === templateWin ? 3 : -3;
  }

  const rowGameType = normalizeGameType(row?.gameType);
  const templateGameType = normalizeGameType(template?.gameType);
  if (rowGameType && templateGameType) {
    signals += 1;
    score += rowGameType === templateGameType ? 2 : -2;
  }

  const rowRef = normalizeRefKey(row?.ref);
  const templateRef = normalizeRefKey(template?.ref);
  if (rowRef && templateRef) {
    signals += 1;
    score += rowRef === templateRef ? 3 : -1;
  }

  return signals ? score : -Infinity;
}

function findBestIndexTemplateOffset(rows = [], recapRows = []) {
  const imageRows = Array.isArray(rows) ? rows : [];
  const templates = Array.isArray(recapRows) ? recapRows : [];
  if (!imageRows.length || imageRows.length > templates.length) return -1;

  let bestOffset = -1;
  let bestScore = -Infinity;
  const maxOffset = templates.length - imageRows.length;

  for (let offset = 0; offset <= maxOffset; offset += 1) {
    let score = 0;
    let comparable = 0;
    for (let idx = 0; idx < imageRows.length; idx += 1) {
      const rowScore = rowIndexTemplateScore(imageRows[idx], templates[offset + idx]);
      if (!Number.isFinite(rowScore)) continue;
      comparable += 1;
      score += rowScore;
    }
    if (!comparable) continue;
    const normalizedScore = score / comparable;
    if (normalizedScore > bestScore) {
      bestScore = normalizedScore;
      bestOffset = offset;
    }
  }

  return bestScore >= 2 ? bestOffset : -1;
}

function findAnchoredTemplateOffset(rows = [], recapRows = []) {
  const imageRows = Array.isArray(rows) ? rows : [];
  const templates = Array.isArray(recapRows) ? recapRows : [];
  if (!imageRows.length || imageRows.length > templates.length) return -1;

  const votes = new Map();
  const maxOffset = templates.length - imageRows.length;
  imageRows.forEach((row, rowIdx) => {
    templates.forEach((template, templateIdx) => {
      const rowDate = ymd(row?.date);
      const templateDate = ymd(template?.date);
      const rowWeek = String(row?.week || "").trim();
      const templateWeek = String(template?.week || "").trim();
      const rowGameNo = Number(row?.gameNo || 0);
      const templateGameNo = Number(template?.gameNo || 0);
      const rowGameType = normalizeGameType(row?.gameType);
      const templateGameType = normalizeGameType(template?.gameType);
      const strong =
        rowDate &&
        templateDate &&
        rowDate === templateDate &&
        rowGameNo > 0 &&
        templateGameNo > 0 &&
        rowGameNo === templateGameNo &&
        (!rowWeek || !templateWeek || rowWeek === templateWeek) &&
        (!rowGameType || !templateGameType || rowGameType === templateGameType);
      if (!strong) return;

      const offset = templateIdx - rowIdx;
      if (offset < 0 || offset > maxOffset) return;
      votes.set(offset, (votes.get(offset) || 0) + 1);
    });
  });

  return [...votes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? -1;
}

function findTemplateSliceOffset(rows = [], recapRows = []) {
  const anchoredOffset = findAnchoredTemplateOffset(rows, recapRows);
  if (anchoredOffset >= 0) return anchoredOffset;
  return findBestIndexTemplateOffset(rows, recapRows);
}

function hasStrongTemplateAnchor(row = {}, template = {}) {
  const rowDate = ymd(row?.date);
  const templateDate = ymd(template?.date);
  const rowWeek = String(row?.week || "").trim();
  const templateWeek = String(template?.week || "").trim();
  const rowGameNo = Number(row?.gameNo || 0);
  const templateGameNo = Number(template?.gameNo || 0);
  const rowRef = normalizeRefKey(row?.ref);
  const templateRef = normalizeRefKey(template?.ref);

  if (rowDate && templateDate && rowDate === templateDate) {
    if (rowGameNo > 0 && templateGameNo > 0 && rowGameNo === templateGameNo) return true;
    if (rowWeek && templateWeek && rowWeek === templateWeek) return true;
  }

  if (rowRef && templateRef && rowRef === templateRef) {
    if (rowGameNo > 0 && templateGameNo > 0 && rowGameNo === templateGameNo) return true;
    if (rowDate && templateDate && rowDate === templateDate) return true;
  }

  return false;
}

function findBestTemplateMatches(rows = [], recapRows = []) {
  const imageRows = Array.isArray(rows) ? rows : [];
  const templates = Array.isArray(recapRows) ? recapRows : [];
  const m = imageRows.length;
  const n = templates.length;

  if (!m || !n) return [];

  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(-Infinity));
  const path = Array.from({ length: m + 1 }, () => Array(n + 1).fill(null));
  dp[0][0] = 0;

  for (let j = 0; j < n; j += 1) {
    if (Number.isFinite(dp[0][j])) {
      dp[0][j + 1] = dp[0][j] - 0.05;
      path[0][j + 1] = { type: "skip-template", i: 0, j };
    }
  }

  for (let i = 0; i < m; i += 1) {
    for (let j = 0; j <= n; j += 1) {
      if (!Number.isFinite(dp[i][j])) continue;

      if (j < n) {
        const skipTemplateScore = dp[i][j] - 0.05;
        if (skipTemplateScore > dp[i][j + 1]) {
          dp[i][j + 1] = skipTemplateScore;
          path[i][j + 1] = { type: "skip-template", i, j };
        }

        const matchScore = dp[i][j] + rowMatchScore(imageRows[i], templates[j]);
        if (matchScore > dp[i + 1][j + 1]) {
          dp[i + 1][j + 1] = matchScore;
          path[i + 1][j + 1] = { type: "match", i, j };
        }
      }

      const skipImageScore = dp[i][j] - 3;
      if (skipImageScore > dp[i + 1][j]) {
        dp[i + 1][j] = skipImageScore;
        path[i + 1][j] = { type: "skip-image", i, j };
      }
    }
  }

  let bestJ = 0;
  let bestScore = -Infinity;
  for (let j = 0; j <= n; j += 1) {
    if (dp[m][j] > bestScore) {
      bestScore = dp[m][j];
      bestJ = j;
    }
  }

  const matches = Array(m).fill(null);
  let i = m;
  let j = bestJ;

  while (i > 0 || j > 0) {
    const step = path[i][j];
    if (!step) break;
    if (step.type === "match") {
      matches[step.i] = { templateIndex: step.j, score: rowMatchScore(imageRows[step.i], templates[step.j]) };
    }
    i = step.i;
    j = step.j;
  }

  return matches;
}

function rebuildFieldsFromTemplate(row = {}, template = {}, fields = [], options = {}) {
  const { preferTemplateValues = false } = options;
  const nextRow = { ...row };
  const templateFilledFields = fields.filter((field) => Boolean(String(template?.[field] || "").trim()));
  const heroSequence = fields
    .map((field) => String(row?.[field] || "").trim())
    .filter((value) => isLikelyHeroText(value));

  if (preferTemplateValues) {
    fields.forEach((field) => {
      nextRow[field] = normalizeHeroValue(template?.[field] || "");
    });
    return nextRow;
  }

  if (templateFilledFields.length && heroSequence.length >= templateFilledFields.length) {
    let cursor = 0;
    fields.forEach((field) => {
      nextRow[field] = String(template?.[field] || "").trim()
        ? normalizeHeroValue(heroSequence[cursor++] || template?.[field] || "")
        : "";
    });
    return nextRow;
  }

  fields.forEach((field) => {
    const templateValue = String(template?.[field] || "").trim();
    const rowValue = String(row?.[field] || "").trim();

    if (!templateValue) {
      nextRow[field] = "";
      return;
    }

    nextRow[field] = isLikelyHeroText(rowValue) ? normalizeHeroValue(rowValue) : normalizeHeroValue(templateValue);
  });

  return nextRow;
}

function repairRowByTemplate(row = {}, template = {}, matchScore = 0, options = {}) {
  const { preferTemplateMeta = false } = options;
  if (!template || matchScore < 4) return row;

  let repaired = {
    ...row,
    date: preferTemplateMeta ? ymd(template?.date || row?.date) : ymd(row?.date || template?.date),
    week: preferTemplateMeta
      ? String(template?.week || row?.week || "").trim()
      : String(row?.week || template?.week || "").trim(),
    gameNo: preferTemplateMeta
      ? (Number(template?.gameNo || row?.gameNo || 0) || row?.gameNo || 1)
      : (Number(row?.gameNo || template?.gameNo || 0) || template?.gameNo || 1),
    gameType: preferTemplateMeta
      ? String(template?.gameType || row?.gameType || "").trim()
      : String(row?.gameType || template?.gameType || "").trim(),
    bruWin: Number(template?.bruWin) === 1 ? 1 : 0,
    ref: preferTemplateMeta
      ? String(template?.ref || row?.ref || "").trim()
      : String(row?.ref || template?.ref || "").trim(),
  };

  repaired = rebuildFieldsFromTemplate(repaired, template, BRU_BLOCK_FIELDS, options);
  repaired = rebuildFieldsFromTemplate(repaired, template, OP_BLOCK_FIELDS, options);

  if (!rowHasAnyBanValues(template)) {
    repaired = clearBanFields(repaired);
  }

  return repaired;
}

function hasNoGaps(values = []) {
  let seenBlank = false;
  for (const value of values) {
    const hasValue = Boolean(String(value || "").trim());
    if (!hasValue) {
      seenBlank = true;
    } else if (seenBlank) {
      return false;
    }
  }
  return true;
}

function realignBlockByTemplate(row = {}, template = {}, fields = []) {
  const values = fields.map((field) => String(row?.[field] || "").trim());
  const templateFilled = fields.map((field) => Boolean(String(template?.[field] || "").trim()));
  const nonEmptyValues = values.filter(Boolean);

  if (!nonEmptyValues.length) return row;
  if (!templateFilled.some(Boolean)) return row;
  if (nonEmptyValues.length !== templateFilled.filter(Boolean).length) return row;
  if (!hasNoGaps(values)) return row;

  const alreadyAligned = fields.every((field, idx) => Boolean(values[idx]) === templateFilled[idx]);
  if (alreadyAligned) return row;

  const nextRow = { ...row };
  let cursor = 0;
  fields.forEach((field, idx) => {
    nextRow[field] = templateFilled[idx] ? (nonEmptyValues[cursor++] || "") : "";
  });
  return nextRow;
}

function getLooseTemplateKey(row = {}) {
  return [
    ymd(row?.date),
    String(row?.week || "").trim(),
    String(row?.gameNo || "").trim(),
    normalizeGameType(row?.gameType),
  ].join("|");
}

function repairSequentialTemplateGaps(rows = [], recapRows = []) {
  const inputRows = Array.isArray(rows) ? rows : [];
  const templates = Array.isArray(recapRows) ? recapRows : [];
  if (!inputRows.length || !templates.length) return inputRows;

  const templateIndexByKey = new Map(templates.map((row, idx) => [getLooseTemplateKey(row), idx]));
  let lastTemplateIndex = -1;

  return inputRows.map((row) => {
    const exactIndex = templateIndexByKey.get(getLooseTemplateKey(row));
    if (Number.isInteger(exactIndex)) {
      lastTemplateIndex = exactIndex;
      return row;
    }

    const nextTemplate = templates[lastTemplateIndex + 1];
    if (nextTemplate && shouldUseIndexTemplate(row, nextTemplate)) {
      lastTemplateIndex += 1;
      return repairRowByTemplate(
        { ...row },
        nextTemplate,
        4,
        { preferTemplateValues: true, preferTemplateMeta: true }
      );
    }

    return row;
  });
}

export function alignImageRowsToRecap(rows = [], recapRows = []) {
  const recapByKey = new Map((recapRows || []).map((row) => [getRowAnchorKey(row), row]));
  const orderedMatches = findBestTemplateMatches(rows, recapRows);
  const indexTemplateOffset = findBestIndexTemplateOffset(rows, recapRows);

  const alignedRows = (rows || []).map((row, idx) => {
    const directTemplate = recapByKey.get(getRowAnchorKey(row));
    const matched = orderedMatches[idx];
    const candidateTemplate = matched ? recapRows?.[matched.templateIndex] : null;
    const indexTemplate = indexTemplateOffset >= 0 ? recapRows?.[indexTemplateOffset + idx] : null;
    const matchScore = matched?.score ?? rowMatchScore(row, candidateTemplate);
    const fallbackTemplate = shouldUseMatchedTemplate(row, candidateTemplate, matchScore)
      ? candidateTemplate
      : null;
    const indexFallbackTemplate = shouldUseIndexTemplate(row, indexTemplate)
      ? indexTemplate
      : null;
    const orderedFallbackTemplate = shouldUseOrderedTemplate(row, candidateTemplate, matchScore)
      ? candidateTemplate
      : null;
    const template = directTemplate || indexFallbackTemplate || fallbackTemplate || orderedFallbackTemplate;
    if (!template) return row;

    const preferTemplateValues =
      Boolean(indexFallbackTemplate) ||
      Boolean(orderedFallbackTemplate) ||
      (
        Boolean(directTemplate) &&
        hasTemplateLayoutDrift(row, template) &&
        countAnchorMatches(row, template) >= 2
      );

    let aligned = repairRowByTemplate(
      { ...row },
      template,
      Math.max(4, matchScore),
      {
        preferTemplateValues,
        preferTemplateMeta: Boolean(directTemplate) || Boolean(indexFallbackTemplate) || Boolean(orderedFallbackTemplate),
      }
    );
    aligned.gameType = aligned.gameType || String(template?.gameType || "").trim();
    aligned = realignBlockByTemplate(aligned, template, BRU_BLOCK_FIELDS);
    aligned = realignBlockByTemplate(aligned, template, OP_BLOCK_FIELDS);
    return aligned;
  });

  return repairSequentialTemplateGaps(alignedRows, recapRows);
}

export function buildTemplateRowsForImageRows(rows = [], recapRows = []) {
  const inputRows = Array.isArray(rows) ? rows : [];
  const templates = Array.isArray(recapRows) ? recapRows : [];
  if (!inputRows.length || !templates.length || inputRows.length > templates.length) return null;

  const offset = findTemplateSliceOffset(inputRows, templates);
  if (offset < 0) return null;

  return inputRows.map((row, idx) => {
    const template = templates[offset + idx];
    return repairRowByTemplate(
      { ...row },
      template,
      4,
      { preferTemplateValues: true, preferTemplateMeta: true }
    );
  });
}

function rowHasAnyBanValues(row = {}) {
  return BAN_FIELDS.some((field) => String(row?.[field] || "").trim());
}

function clearBanFields(row = {}) {
  const nextRow = { ...row };
  BAN_FIELDS.forEach((field) => {
    nextRow[field] = "";
  });
  return nextRow;
}

function recapTemplatesHaveAnyBans(recapRows = []) {
  return (Array.isArray(recapRows) ? recapRows : []).some((row) =>
    rowHasAnyBanValues(row)
  );
}

export function clearBanFieldsWhenTemplatesHaveNoBans(rows = [], recapRows = []) {
  const inputRows = Array.isArray(rows) ? rows : [];
  const templates = Array.isArray(recapRows) ? recapRows : [];
  if (!templates.length || recapTemplatesHaveAnyBans(templates)) return inputRows;

  return inputRows.map(clearBanFields);
}

function gatherUniqueHeroes(row, fields) {
  const seen = new Set();
  const heroes = [];
  fields.forEach((field) => {
    const rawValue = String(row?.[field] || "").trim();
    if (!rawValue || !isLikelyHeroText(rawValue)) return;
    const normalized = normalizeHeroValue(rawValue);
    const key = normalizeHeroKey(normalized);
    if (!key || seen.has(key)) return;
    seen.add(key);
    heroes.push(normalized);
  });
  return heroes;
}

function findRefCandidate(row) {
  const directRef = String(row?.ref || "").trim();
  if (isLikelyRefValue(directRef)) return directRef;
  for (const field of SHIFTABLE_FIELDS) {
    const value = String(row?.[field] || "").trim();
    if (isLikelyRefValue(value)) return value;
  }
  return directRef;
}

function placeBruHeroes(row, heroes) {
  const banCount = Math.min(4, Math.max(0, heroes.length - 5));
  const next = { ...row };
  BRU_BLOCK_FIELDS.forEach((field, idx) => {
    if (idx < 4) {
      next[field] = idx < banCount ? (heroes[idx] || "") : "";
    } else {
      const heroIdx = banCount + (idx - 4);
      next[field] = heroes[heroIdx] || "";
    }
  });
  return next;
}

function placeOpHeroes(row, heroes) {
  const banCount = Math.min(4, Math.max(0, heroes.length - 5));
  const next = { ...row };
  OP_BLOCK_FIELDS.forEach((field, idx) => {
    if (idx < 5) {
      next[field] = heroes[idx] || "";
    } else {
      const banIdx = idx - 5;
      next[field] = banIdx < banCount ? (heroes[5 + banIdx] || "") : "";
    }
  });
  return next;
}

function rebalanceAcrossSides(bruHeroes, opHeroes) {
  const bru = [...bruHeroes];
  const op = [...opHeroes];

  while (bru.length > 5 && op.length < 5) op.unshift(bru.pop());
  while (op.length > 5 && bru.length < 5) bru.push(op.shift());

  while (bru.length > 9 && op.length < 9) op.unshift(bru.pop());
  while (op.length > 9 && bru.length < 9) bru.push(op.shift());

  return { bru: bru.slice(0, 9), op: op.slice(0, 9) };
}

function buildDeterministicLayout(row) {
  const bruGathered = gatherUniqueHeroes(row, BRU_BLOCK_FIELDS);
  const opGathered = gatherUniqueHeroes(row, OP_BLOCK_FIELDS);
  if (!bruGathered.length && !opGathered.length) return null;

  const { bru: bruHeroes, op: opHeroes } = rebalanceAcrossSides(bruGathered, opGathered);

  const ref = findRefCandidate(row);
  let next = { ...row };
  next = placeBruHeroes(next, bruHeroes);
  next = placeOpHeroes(next, opHeroes);
  next.ref = ref;
  next.bruWin = Number(row?.bruWin) === 1 ? 1 : 0;
  return next;
}

function repairCompressedBanRows(rows = []) {
  const inputRows = Array.isArray(rows) ? rows : [];
  return inputRows.map((row) => buildDeterministicLayout(row) || row);
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });
}

const COLUMN_RATIOS = (() => {
  const widths = ROW_COLUMNS.map((column) => COLUMN_WIDTHS[column] || 100);
  const total = widths.reduce((acc, value) => acc + value, 0) || 1;
  let cumulative = 0;
  return ROW_COLUMNS.map((column, idx) => {
    const start = cumulative / total;
    cumulative += widths[idx];
    return { column, start, end: cumulative / total };
  });
})();

function findColumnByXRatio(xRatio) {
  for (const range of COLUMN_RATIOS) {
    if (xRatio >= range.start && xRatio < range.end) return range.column;
  }
  return COLUMN_RATIOS[COLUMN_RATIOS.length - 1]?.column || null;
}

function clusterWordsByRow(words, rowGapThreshold = 14) {
  if (!words.length) return [];
  const sorted = [...words].sort((a, b) => (a.bbox?.y0 || 0) - (b.bbox?.y0 || 0));
  const groups = [];
  let current = [];
  let lastTop = -Infinity;
  let lastBottom = -Infinity;

  for (const word of sorted) {
    const top = word.bbox?.y0 || 0;
    const bottom = word.bbox?.y1 || top;
    const overlaps = current.length && top <= lastBottom + 4;
    const closeEnough = current.length && top - lastTop <= rowGapThreshold;
    if (overlaps || closeEnough) {
      current.push(word);
      lastTop = Math.max(lastTop, top);
      lastBottom = Math.max(lastBottom, bottom);
    } else {
      if (current.length) groups.push(current);
      current = [word];
      lastTop = top;
      lastBottom = bottom;
    }
  }
  if (current.length) groups.push(current);
  return groups;
}

function rowGroupLooksLikeHeader(rowWords) {
  const text = rowWords.map((word) => word.text || "").join(" ").toLowerCase();
  if (!text) return true;
  const looksLikeHeader = /(date|week|game|type|ban|win|dsl|jug|mid|sup|adl|opp|opp)/i.test(text);
  const hasDate = /\d{6,8}/.test(text);
  return looksLikeHeader && !hasDate;
}

function tesseractRowFromWords(rowWords, imageWidth) {
  const cells = {};
  ROW_COLUMNS.forEach((column) => { cells[column] = ""; });

  rowWords.forEach((word) => {
    const text = String(word.text || "").trim();
    if (!text) return;
    const x0 = word.bbox?.x0 || 0;
    const x1 = word.bbox?.x1 || x0;
    const centerX = (x0 + x1) / 2;
    const xRatio = imageWidth > 0 ? centerX / imageWidth : 0;
    const column = findColumnByXRatio(xRatio);
    if (!column) return;
    cells[column] = cells[column] ? `${cells[column]} ${text}` : text;
  });

  const out = { ...cells };
  out.date = normalizeDraftImageDate((cells.date.match(/\d{8}/) || [""])[0]);
  out.week = (cells.week.match(/\d{1,2}/) || [""])[0];
  const gameMatch = cells.gameNo.match(/\d{1,2}/);
  out.gameNo = gameMatch ? Number(gameMatch[0]) : 0;
  out.gameType = cells.gameType.replace(/[^A-Za-z]/g, "").trim();
  const winMatch = cells.bruWin.match(/[01]/);
  out.bruWin = winMatch ? Number(winMatch[0]) : 0;
  out.ref = cells.ref.replace(/[^A-Za-z0-9!#]/g, "").toUpperCase().slice(0, 6);

  HERO_COLUMNS.forEach((field) => {
    const raw = String(cells[field] || "").replace(/[^A-Za-z'’\-\s]/g, "").trim();
    out[field] = raw ? normalizeHeroValue(raw) : "";
  });

  return out;
}

function tesseractRowHasContent(row) {
  if (!row) return false;
  if (row.date || row.ref) return true;
  return HERO_COLUMNS.some((field) => String(row[field] || "").trim());
}

async function preprocessImageForTesseract(file, scale = 1.6) {
  if (typeof document === "undefined") return URL.createObjectURL(file);
  const url = URL.createObjectURL(file);
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("preprocess: image load failed"));
    el.src = url;
  });
  URL.revokeObjectURL(url);

  const w = (img.naturalWidth || img.width) * scale;
  const h = (img.naturalHeight || img.height) * scale;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas.toDataURL("image/png");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  const data = ctx.getImageData(0, 0, w, h);
  const pixels = data.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const gray = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
    const contrasted = (gray - 128) * 1.35 + 128;
    const clamped = Math.max(0, Math.min(255, contrasted));
    pixels[i] = clamped;
    pixels[i + 1] = clamped;
    pixels[i + 2] = clamped;
  }
  ctx.putImageData(data, 0, 0);

  return { dataUrl: canvas.toDataURL("image/png"), width: w, height: h, originalWidth: img.naturalWidth || img.width, originalHeight: img.naturalHeight || img.height };
}

async function parseDraftImageWithTesseract(file, options = {}) {
  const { onProgress } = options;
  const preprocessed = await preprocessImageForTesseract(file);

  const TesseractModule = await import("tesseract.js");
  const createWorker = TesseractModule.createWorker || TesseractModule.default?.createWorker;
  if (typeof createWorker !== "function") {
    throw new Error("tesseract.js: createWorker not available");
  }

  const worker = await createWorker("eng", 1, {
    logger: (msg) => {
      if (typeof onProgress === "function" && msg?.status) {
        onProgress(msg);
      }
    },
  });

  try {
    if (typeof worker.setParameters === "function") {
      await worker.setParameters({
        tessedit_pageseg_mode: "11",
        preserve_interword_spaces: "1",
      }).catch(() => {});
    }

    const result = await worker.recognize(preprocessed.dataUrl);
    const rawWords = result?.data?.words || [];
    const words = rawWords.filter((word) => {
      const text = String(word.text || "").trim();
      if (!text) return false;
      if (text.length < 2 && !/[01]/.test(text)) return false;
      if ((word.confidence || 0) < 10) return false;
      return true;
    });

    if (typeof console !== "undefined") {
      console.log("[Tesseract] raw words:", rawWords.length, "| filtered:", words.length, "| sample:", words.slice(0, 30).map((w) => `${w.text}@${Math.round(w.confidence)}`).join(", "));
    }

    if (!words.length) return [];

    const rowGroups = clusterWordsByRow(words);
    if (typeof console !== "undefined") {
      console.log("[Tesseract] row groups:", rowGroups.length);
    }

    const stamp = Date.now();
    const rows = rowGroups
      .filter((group) => !rowGroupLooksLikeHeader(group))
      .map((group, idx) => ({
        ...tesseractRowFromWords(group, preprocessed.width),
        _id: `tesseract-row-${idx}-${stamp}`,
        _source: "image",
      }))
      .filter(tesseractRowHasContent)
      .map((row, idx) => ({
        ...row,
        gameNo: row.gameNo || idx + 1,
      }));

    return rows;
  } finally {
    await worker.terminate().catch(() => {});
  }
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

async function chunkImageByRows(dataUrl, options = {}) {
  const {
    maxChunkPixels = 550,
    headerRatio = 0.04,
    mimeType = "image/jpeg",
    quality = 0.92,
    targetWidth = 2048,
    maxUpscale = 2,
  } = options;
  if (typeof document === "undefined" || typeof Image === "undefined") {
    return [dataUrl];
  }

  let img;
  try {
    img = await loadImageElement(dataUrl);
  } catch {
    return [dataUrl];
  }

  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) return [dataUrl];

  // Upscale modest-resolution screenshots so the (small) hero-name text renders
  // larger for the vision model. Detail "high" tiles the image, so a wider image
  // means each cell is captured at more pixels — measurably better text reads.
  // Only ever scale up, never down, and cap the factor to avoid huge payloads.
  const scale = Math.min(maxUpscale, Math.max(1, targetWidth / w));

  const headerHeight = Math.max(30, Math.min(80, Math.round(h * headerRatio)));
  const bodyHeight = h - headerHeight;
  if (bodyHeight <= maxChunkPixels && scale === 1) return [dataUrl];

  const numChunks = Math.max(1, Math.ceil(bodyHeight / maxChunkPixels));
  const chunkPixels = Math.ceil(bodyHeight / numChunks);
  const scaledW = Math.round(w * scale);
  const scaledHeader = Math.round(headerHeight * scale);
  const chunks = [];

  for (let i = 0; i < numChunks; i += 1) {
    const startY = headerHeight + i * chunkPixels;
    const endY = Math.min(h, headerHeight + (i + 1) * chunkPixels);
    const sliceH = endY - startY;
    if (sliceH <= 0) continue;

    const scaledSlice = Math.round(sliceH * scale);
    const canvas = document.createElement("canvas");
    canvas.width = scaledW;
    canvas.height = scaledHeader + scaledSlice;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;
    if (ctx.imageSmoothingEnabled !== undefined) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
    }

    ctx.drawImage(img, 0, 0, w, headerHeight, 0, 0, scaledW, scaledHeader);
    ctx.drawImage(img, 0, startY, w, sliceH, 0, scaledHeader, scaledW, scaledSlice);
    chunks.push(canvas.toDataURL(mimeType, quality));
  }

  return chunks.length ? chunks : [dataUrl];
}

async function callParseDraftImageApi({ imageDataUrl, rowHints = [], heroNames = [], verify = true }) {
  const response = await fetch("/api/parse-draft-image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ imageDataUrl, rowHints, heroNames, verify }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || "Image parse failed");
  }
  return {
    rows: normalizeImageRows(payload?.rows),
    notes: Array.isArray(payload?.notes) ? payload.notes : [],
    model: String(payload?.model || ""),
  };
}

const HERO_NAMES_LIST = (Array.isArray(heroList) ? heroList : [])
  .map((hero) => String(hero?.name || "").trim())
  .filter(Boolean);

function firstValue(source = {}, keys = []) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

export function buildDraftRecapRow(game = {}, gameNo = 1, idSuffix = "") {
  const hasDraftRecapBanSource = Array.isArray(game?.draftRecapFsBan) || Array.isArray(game?.draftRecapOpBan);
  const fsBan = hasDraftRecapBanSource
    ? (Array.isArray(game?.draftRecapFsBan) ? game.draftRecapFsBan : [])
    : (Array.isArray(game?.fsBan) ? game.fsBan : []);
  const opBan = hasDraftRecapBanSource
    ? (Array.isArray(game?.draftRecapOpBan) ? game.draftRecapOpBan : [])
    : (Array.isArray(game?.opBan) ? game.opBan : []);
  const dateValue = firstValue(game, ["Date", "date"]);
  const competitor = firstValue(game, ["competitor", "Competitor", "opponent", "Opponent"]);

  return {
    date: ymd(dateValue),
    week: weekNum(firstValue(game, ["week", "Week"])),
    gameNo,
    gameType: String(firstValue(game, ["type", "Type"]) || "").trim(),
    bruBan1: String(fsBan[0] || "").trim(),
    bruBan2: String(fsBan[1] || "").trim(),
    bruBan3: String(fsBan[2] || "").trim(),
    bruBan4: String(fsBan[3] || "").trim(),
    bruDSL: pickRoleValue(game, "fsByRole", "fsPick", "fsPosition", "DSL"),
    bruJUG: pickRoleValue(game, "fsByRole", "fsPick", "fsPosition", "JUG"),
    bruMID: pickRoleValue(game, "fsByRole", "fsPick", "fsPosition", "MID"),
    bruADL: pickRoleValue(game, "fsByRole", "fsPick", "fsPosition", "ADL"),
    bruSUP: pickRoleValue(game, "fsByRole", "fsPick", "fsPosition", "SUP"),
    bruWin: Number(game?.win || 0) === 1 ? 1 : 0,
    opDSL: pickRoleValue(game, "opByRole", "opPick", "opPosition", "DSL"),
    opJUG: pickRoleValue(game, "opByRole", "opPick", "opPosition", "JUG"),
    opMID: pickRoleValue(game, "opByRole", "opPick", "opPosition", "MID"),
    opADL: pickRoleValue(game, "opByRole", "opPick", "opPosition", "ADL"),
    opSUP: pickRoleValue(game, "opByRole", "opPick", "opPosition", "SUP"),
    opBan1: String(opBan[0] || "").trim(),
    opBan2: String(opBan[1] || "").trim(),
    qi: String(opBan[2] || "").trim(),
    biron: String(opBan[3] || "").trim(),
    ref: String(firstValue(game, ["match", "Match Win"]) || "").trim(),
    _id: `xlsx-${dateValue || "no-date"}-${competitor || "no-team"}-${idSuffix || gameNo}`,
    _source: "xlsx",
  };
}

/**
 * A scouted row (Specter_Scrim) in the recap's shape.
 *
 * The sheet is Team A vs Team B with no FS in it, so `team` decides which side
 * fills the TEAM columns; the other takes OPP. The labels stay honest because
 * they say TEAM / OPP rather than naming us.
 */
export function buildScoutedRecapRow(row = {}, team = "", gameNo = 1, idSuffix = "") {
  const wanted = String(team || "").trim().toUpperCase();
  const flip = wanted && String(row.teamB || "").trim().toUpperCase() === wanted;
  const mine = flip
    ? { code: row.teamB, byRole: row.bByRole, bans: row.bBans }
    : { code: row.teamA, byRole: row.aByRole, bans: row.aBans };
  const theirs = flip
    ? { code: row.teamA, byRole: row.aByRole, bans: row.aBans }
    : { code: row.teamB, byRole: row.bByRole, bans: row.bBans };

  const at = (side, role) => String(side.byRole?.[role] || "").trim();
  const ban = (side, i) => String((side.bans || [])[i] || "").trim();
  const won =
    String(row.winner || "").trim().toUpperCase() === String(mine.code || "").trim().toUpperCase();

  return {
    date: ymd(row.Date),
    week: weekNum(row.week),
    gameNo,
    gameType: String(row.type || "").trim(),
    bruBan1: ban(mine, 0),
    bruBan2: ban(mine, 1),
    bruBan3: ban(mine, 2),
    bruBan4: ban(mine, 3),
    bruDSL: at(mine, "DSL"),
    bruJUG: at(mine, "JUG"),
    bruMID: at(mine, "MID"),
    bruADL: at(mine, "ADL"),
    bruSUP: at(mine, "SUP"),
    bruWin: won ? 1 : 0,
    opDSL: at(theirs, "DSL"),
    opJUG: at(theirs, "JUG"),
    opMID: at(theirs, "MID"),
    opADL: at(theirs, "ADL"),
    opSUP: at(theirs, "SUP"),
    opBan1: ban(theirs, 0),
    opBan2: ban(theirs, 1),
    qi: ban(theirs, 2),
    biron: ban(theirs, 3),
    // The recap has no team column, so the sheet's spare one names the pairing.
    ref: `${mine.code || "?"} vs ${theirs.code || "?"}`,
    _id: `specter-${row.Date || "no-date"}-${mine.code || "?"}-${theirs.code || "?"}-${idSuffix || gameNo}`,
    _source: "specter",
  };
}

export default function DraftRecapPage({ games = [], excelFile = null }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const exportImageRef = useRef(null);
  const tableScrollRef = useRef(null);

  // มีไฟล์อยู่แล้วให้เริ่มที่ตาราง XLSX เลย ไม่ต้องให้ผู้ใช้เดาว่าทำไมหน้าว่าง
  const [tableSource, setTableSource] = useState(() => (games?.length ? "xlsx" : "image"));
  const sourceTouched = useRef(false);
  useEffect(() => {
    if (sourceTouched.current) return;
    if (games?.length) setTableSource("xlsx");
  }, [games]);
  const chooseSource = (next) => {
    sourceTouched.current = true;
    setTableSource(next);
  };
  const [ocrEngine, setOcrEngine] = useState("api");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [teamSearch, setTeamSearch] = useState("");
  const [ocrRows, setOcrRows] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrStatus, setOcrStatus] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const recapRows = useMemo(() => {
    const filteredGames = (Array.isArray(games) ? games : [])
      .filter((game) => {
        const type = String(firstValue(game, ["type", "Type"]) || "").trim().toLowerCase();
        return type === "rpl" || type === "scrim";
      })
      .filter((game) => {
        const date = ymd(firstValue(game, ["Date", "date"]));
        if (dateFrom && date < dateFrom) return false;
        if (dateTo && date > dateTo) return false;
        const competitor = String(firstValue(game, ["competitor", "Competitor", "opponent", "Opponent"]) || "");
        if (teamSearch && !competitor.toLowerCase().includes(teamSearch.toLowerCase().trim())) return false;
        return true;
      })
      .sort((a, b) => (
        String(firstValue(a, ["Date", "date"]) || "").localeCompare(String(firstValue(b, ["Date", "date"]) || "")) ||
        String(firstValue(a, ["competitor", "Competitor", "opponent", "Opponent"]) || "").localeCompare(String(firstValue(b, ["competitor", "Competitor", "opponent", "Opponent"]) || ""))
      ));

    const gameNoBySeries = new Map();
    return filteredGames.map((game) => {
      const key = `${String(firstValue(game, ["Date", "date"]) || "")}|${String(firstValue(game, ["competitor", "Competitor", "opponent", "Opponent"]) || "")}`;
      const nextGameNo = (gameNoBySeries.get(key) || 0) + 1;
      gameNoBySeries.set(key, nextGameNo);
      return buildDraftRecapRow(game, nextGameNo, nextGameNo);
    });
  }, [games, dateFrom, dateTo, teamSearch]);

  // ชีทส่องอ่านตอนกดดูครั้งแรก — ไฟล์เวิร์กบุ๊กใหญ่ ไม่ต้องรอตอนเปิดหน้า
  const { log: specterLog, loading: specterLoading } = useSeasonLog(
    excelFile,
    tableSource === "specter",
    parseSpecterScrimP
  );

  const scoutedRows = useMemo(() => {
    const rows = specterLog?.games || [];
    const q = teamSearch.trim().toLowerCase();
    const seen = new Map();
    return rows
      .filter((row) => {
        const date = ymd(row.Date);
        if (dateFrom && date < dateFrom) return false;
        if (dateTo && date > dateTo) return false;
        if (!q) return true;
        return [row.teamA, row.teamB].some((t) => String(t || "").toLowerCase().includes(q));
      })
      .map((row) => {
        // Searching a team puts that team in the TEAM columns.
        const team = q
          ? [row.teamA, row.teamB].find((t) => String(t || "").toLowerCase().includes(q)) || ""
          : "";
        const key = `${ymd(row.Date)}|${row.teamA}|${row.teamB}`;
        const next = (seen.get(key) || 0) + 1;
        seen.set(key, next);
        return buildScoutedRecapRow(row, team, next, next);
      });
  }, [specterLog, dateFrom, dateTo, teamSearch]);

  const shownRows = useMemo(() => {
    if (tableSource === "xlsx") return recapRows;
    if (tableSource === "specter") return scoutedRows;
    return ocrRows;
  }, [ocrRows, recapRows, scoutedRows, tableSource]);

  const runImageParse = async (file) => {
    setOcrBusy(true);
    setOcrStatus("กำลังเตรียมรูป...");
    try {
      const stamp = Date.now();
      let rawMerged = [];
      let allNotes = [];
      let usedModel = "";

      if (ocrEngine === "local") {
        setOcrStatus("กำลังโหลด Tesseract.js (ครั้งแรกอาจนาน)...");
        const tesseractRows = await parseDraftImageWithTesseract(file, {
          onProgress: (msg) => {
            const pct = typeof msg?.progress === "number" ? Math.round(msg.progress * 100) : null;
            const status = msg?.status ? String(msg.status) : "";
            if (status === "recognizing text" && pct !== null) {
              setOcrStatus(`Tesseract OCR: ${pct}%`);
            } else if (status) {
              setOcrStatus(`Tesseract: ${status}${pct !== null ? ` ${pct}%` : ""}`);
            }
          },
        });
        rawMerged = tesseractRows.map((row, idx) => ({
          ...row,
          _id: `tesseract-row-${idx}-${stamp}`,
          _source: "image",
        }));
        usedModel = "tesseract.js (local)";
        allNotes = ["Local OCR (Tesseract.js)"];
      } else {
        const fullDataUrl = await fileToDataUrl(file);
        const chunks = await chunkImageByRows(fullDataUrl);
        const rowHints = [];

        setOcrStatus(
          chunks.length > 1
            ? `กำลังอ่านรูปแบบขนาน ${chunks.length} ส่วน (2-pass)...`
            : "กำลังอ่านข้อมูลจากรูป (2-pass)..."
        );

        const results = await Promise.all(
          chunks.map((chunkDataUrl) =>
            callParseDraftImageApi({
              imageDataUrl: chunkDataUrl,
              rowHints,
              heroNames: HERO_NAMES_LIST,
              verify: true,
            })
          )
        );

        let globalRowIdx = 0;
        rawMerged = results.flatMap((result, chunkIdx) =>
          result.rows.map((row, rowIdx) => {
            const fallbackGameNo = globalRowIdx + 1;
            globalRowIdx += 1;
            return {
              ...row,
              _id: `image-row-${chunkIdx}-${rowIdx}-${stamp}`,
              gameNo: Number(row?.gameNo) || fallbackGameNo,
            };
          })
        );
        allNotes = results.flatMap((result) => result.notes);
        usedModel = results.find((result) => result.model)?.model || "";
      }

      const repaired = repairCompressedBanRows(rawMerged);
      const templateRows = (recapRows && recapRows.length)
        ? buildTemplateRowsForImageRows(repaired, recapRows)
        : null;
      const alignedRaw = (recapRows && recapRows.length)
        ? (templateRows || alignImageRowsToRecap(repaired, recapRows))
        : repaired;
      const aligned = clearBanFieldsWhenTemplatesHaveNoBans(alignedRaw, recapRows);
      const sanitized = aligned.map((row) => {
        const out = { ...row };
        const seenKeys = new Set();
        HERO_COLUMNS.forEach((field) => {
          const value = String(out[field] || "").trim();
          if (!value) {
            out[field] = "";
            return;
          }
          // Fuzzy-snap to the catalog first: OCR/vision variants like "Glidur"→
          // "Gildur", "Fiorentino"→"Florentino", "Wonder Woman"→"Wonder Women"
          // should be recovered, not blanked. correctHeroSpelling returns the
          // canonical name on a confident match, otherwise the raw value.
          const corrected = correctHeroSpelling(value);
          const key = normalizeHeroKey(corrected);
          if (!heroCanonicalMap.has(key)) {
            out[field] = "";
            return;
          }
          if (seenKeys.has(key)) {
            out[field] = "";
            return;
          }
          seenKeys.add(key);
          out[field] = heroCanonicalMap.get(key) || corrected;
        });
        return out;
      });
      const mergedRows = sanitized.map((row, idx) => ({
        ...row,
        _id: row?._id || `image-row-merged-${idx}-${stamp}`,
        _source: "image",
      }));

      setOcrRows(mergedRows);
      setTableSource("image");
      setEditingCell(null);
      const modelTag = usedModel ? ` | model: ${usedModel}` : "";
      setOcrStatus(
        mergedRows.length
          ? `อ่านสำเร็จ ${mergedRows.length} แถว${modelTag}${allNotes.length ? ` | ${allNotes.slice(0, 2).join(" | ")}` : ""}`
          : recapRows.length
            ? `AI ยังอ่านแถวจากรูปนี้ไม่เจอ${modelTag}`
            : `อ่านไม่เจอข้อมูลในรูป${modelTag}`
      );
    } catch (error) {
      console.error("Draft image parse error:", error);
      setOcrStatus(`อ่านรูปไม่สำเร็จ: ${error?.message || "กรุณาลองใหม่"}`);
    } finally {
      setOcrBusy(false);
    }
  };

  const processFile = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setUploadedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    await runImageParse(file);
  };

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    await processFile(file);
    if (event?.target) event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setDragActive(false);
    if (ocrBusy) return;
    const file = event?.dataTransfer?.files?.[0];
    if (file) await processFile(file);
  };

  const clearImageMode = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setUploadedFile(null);
    setPreviewUrl("");
    setOcrRows([]);
    setOcrStatus("");
    setEditingCell(null);
    setTableSource("xlsx");
  };

  const addImageRow = () => {
    setTableSource("image");
    setOcrRows((prev) => [...prev, createEmptyRow(prev.length)]);
  };

  const updateImageCell = (rowId, field, value) => {
    setOcrRows((prev) => prev.map((row) => {
      if (row?._id !== rowId) return row;
      if (field === "date") return { ...row, date: ymd(value) };
      if (field === "week") return { ...row, week: String(value || "").replace(/[^\d]/g, "") };
      if (field === "gameNo") return { ...row, gameNo: Number(String(value || "").replace(/[^\d]/g, "")) || 1 };
      if (field === "gameType") return { ...row, gameType: String(value || "") };
      if (field === "bruWin") return { ...row, bruWin: Number(String(value || "").replace(/[^\d]/g, "")) === 1 ? 1 : 0 };
      if (HERO_COLUMNS.includes(field)) return { ...row, [field]: normalizeHeroValue(value) };
      return { ...row, [field]: String(value || "") };
    }));
  };

  const exportCsv = async () => {
    const data = buildExportRows(shownRows);
    if (!data.length) return;
    const XLSX = await import("xlsx");
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `draft_recap_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportXlsx = async () => {
    const data = buildExportRows(shownRows);
    if (!data.length) return;
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "DraftRecap");
    XLSX.writeFile(wb, `draft_recap_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPng = async () => {
    const card = exportImageRef.current;
    const scroller = tableScrollRef.current;
    if (!shownRows.length || !card) return;

    // The recap table lives inside a scroll box (maxHeight + overflow:auto) nested
    // in a card with overflow:hidden, so a straight capture only grabs the visible
    // window — rows below the fold and the right edge of the 2440px-wide table get
    // clipped. Temporarily expand both so html2canvas lays out and sees the FULL
    // table, then restore the original styles (always, even on error).
    const prevCard = {
      overflow: card.style.overflow,
      width: card.style.width,
      maxWidth: card.style.maxWidth,
    };
    const prevScroller = scroller
      ? { overflow: scroller.style.overflow, maxHeight: scroller.style.maxHeight }
      : null;

    card.style.overflow = "visible";
    card.style.width = "fit-content";
    card.style.maxWidth = "none";
    if (scroller) {
      scroller.style.overflow = "visible";
      scroller.style.maxHeight = "none";
    }

    // Wait two frames so the browser reflows the expanded layout before capture.
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    );

    try {
      const html2canvas = (await import("html2canvas")).default;
      const fullWidth = card.scrollWidth;
      const fullHeight = card.scrollHeight;
      const canvas = await html2canvas(card, {
        backgroundColor: recapTheme.bg,
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
      });

      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `draft_recap_${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
    } catch (error) {
      console.error("Draft recap image export error:", error);
      setOcrStatus(`โหลดรูปไม่สำเร็จ: ${error?.message || "กรุณาลองใหม่"}`);
    } finally {
      card.style.overflow = prevCard.overflow;
      card.style.width = prevCard.width;
      card.style.maxWidth = prevCard.maxWidth;
      if (scroller && prevScroller) {
        scroller.style.overflow = prevScroller.overflow;
        scroller.style.maxHeight = prevScroller.maxHeight;
      }
    }
  };

  const shell = {
    minHeight: "100vh",
    background: `
      radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,16,38,.18) 0%, transparent 50%),
      radial-gradient(ellipse 60% 40% at 90% 100%, rgba(255,16,38,.10) 0%, transparent 55%),
      linear-gradient(180deg, #060606 0%, #0a0608 100%)
    `,
    color: recapTheme.text,
    paddingBottom: 40,
  };

  const stickyBar = {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "rgba(8,8,8,.78)",
    backdropFilter: "blur(24px) saturate(140%)",
    WebkitBackdropFilter: "blur(24px) saturate(140%)",
    borderBottom: "1px solid rgba(255,255,255,.06)",
    padding: "12px 24px",
  };

  const card = {
    background: "linear-gradient(180deg, rgba(20,20,20,.78) 0%, rgba(12,12,12,.78) 100%)",
    border: "1px solid rgba(255,255,255,.06)",
    borderRadius: 14,
    padding: 16,
    boxShadow: "0 6px 20px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.04)",
  };

  const cardLabel = {
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: ".22em",
    color: recapTheme.textMuted,
    textTransform: "uppercase",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
  };

  const cardLabelDot = {
    display: "inline-block",
    width: 14,
    height: 2,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${recapTheme.primary} 0%, transparent 100%)`,
  };

  const inputLabel = {
    fontSize: 10,
    fontWeight: 800,
    color: recapTheme.textMuted,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: ".10em",
  };

  const inputBase = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.06)",
    background: "rgba(255,255,255,.03)",
    color: recapTheme.text,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    fontWeight: 600,
  };

  const segmented = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 4,
    padding: 4,
    background: "rgba(255,255,255,.03)",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.05)",
  };

  const segmentBtn = (active) => ({
    padding: "8px 10px",
    borderRadius: 8,
    border: "none",
    background: active ? `linear-gradient(135deg, ${recapTheme.primary} 0%, #c95400 100%)` : "transparent",
    color: active ? "#fff" : recapTheme.textDim,
    fontWeight: 800,
    fontSize: 12,
    cursor: "pointer",
    transition: "all .15s",
    boxShadow: active ? "0 4px 12px rgba(255,16,38,.28)" : "none",
  });

  const ghostBtn = (disabled) => ({
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.08)",
    background: "rgba(255,255,255,.04)",
    color: recapTheme.text,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    transition: "all .15s",
    whiteSpace: "nowrap",
  });

  const primaryBtn = (disabled) => ({
    padding: "9px 16px",
    fontSize: 12,
    fontWeight: 900,
    borderRadius: 10,
    border: `1px solid ${recapTheme.border}`,
    background: `linear-gradient(135deg, ${recapTheme.primary} 0%, #c95400 100%)`,
    color: "#fff",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    transition: "all .15s",
    boxShadow: disabled ? "none" : "0 6px 16px rgba(255,16,38,.24)",
    letterSpacing: ".02em",
    whiteSpace: "nowrap",
  });

  const dropzone = (active, busy) => ({
    border: `2px dashed ${active ? recapTheme.primary : "rgba(255,255,255,.14)"}`,
    borderRadius: 12,
    padding: "30px 12px",
    textAlign: "center",
    cursor: busy ? "wait" : "pointer",
    background: active
      ? "radial-gradient(circle at 50% 40%, rgba(255,16,38,.16) 0%, rgba(255,16,38,.04) 70%)"
      : "linear-gradient(180deg, rgba(255,255,255,.025) 0%, rgba(255,255,255,.01) 100%)",
    transition: "all .18s",
    opacity: busy ? 0.7 : 1,
    boxShadow: active ? `inset 0 0 24px rgba(255,16,38,.18)` : "none",
  });

  const rowsBadge = {
    fontSize: 11,
    fontWeight: 900,
    padding: "6px 12px",
    borderRadius: 999,
    background: "linear-gradient(135deg, rgba(255,16,38,.18) 0%, rgba(255,16,38,.06) 100%)",
    color: recapTheme.primaryLight,
    border: `1px solid ${recapTheme.border}`,
    letterSpacing: ".04em",
    display: "inline-flex",
    alignItems: "center",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)",
  };

  const separator = {
    width: 1,
    height: 22,
    background: "rgba(255,255,255,.08)",
    margin: "0 4px",
  };

  const tableCard = {
    background: "linear-gradient(180deg, rgba(14,14,14,.94) 0%, rgba(8,8,8,.94) 100%)",
    border: "1px solid rgba(255,255,255,.07)",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 18px 44px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.04)",
  };

  const groupTh = {
    padding: "8px 6px 6px",
    fontSize: 9,
    textAlign: "center",
    borderBottom: `1px solid rgba(255,255,255,.08)`,
    position: "sticky",
    top: 0,
    zIndex: 2,
    fontWeight: 900,
    letterSpacing: ".22em",
    lineHeight: 1,
    textTransform: "uppercase",
  };

  const th = {
    padding: "10px 6px",
    fontSize: 10,
    textAlign: "center",
    border: `1px solid ${recapTheme.borderSoft}`,
    borderTop: "none",
    position: "sticky",
    top: 26,
    zIndex: 1,
    whiteSpace: "normal",
    fontWeight: 900,
    letterSpacing: ".02em",
    lineHeight: 1.15,
    color: recapTheme.text,
  };

  const td = {
    padding: "7px 6px",
    fontSize: 11,
    textAlign: "center",
    border: `1px solid rgba(255,255,255,.06)`,
    color: recapTheme.text,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  };

  const renderEmptyState = () => (
    <div style={{ padding: "80px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 18, textAlign: "center" }}>
      <div style={{ position: "relative" }}>
        <div style={{ position: "absolute", inset: -12, borderRadius: 24, background: `radial-gradient(circle, ${recapTheme.primaryGlow} 0%, transparent 70%)`, filter: "blur(8px)" }} />
        <div style={{
          position: "relative",
          width: 76,
          height: 76,
          borderRadius: 20,
          background: "linear-gradient(135deg, rgba(255,16,38,.18) 0%, rgba(255,16,38,.04) 100%)",
          border: "1px solid rgba(255,16,38,.26)",
          display: "grid",
          placeItems: "center",
          fontSize: 32,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,.10), 0 8px 22px rgba(255,16,38,.18)",
        }}>
          📋
        </div>
      </div>
      <div>
        <div style={{
          fontSize: 19,
          fontWeight: 900,
          letterSpacing: "-.01em",
          background: `linear-gradient(135deg, #ffffff 0%, ${recapTheme.primaryLight} 100%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}>ยังไม่มีข้อมูลให้แสดง</div>
        <div style={{ fontSize: 13, color: recapTheme.textDim, marginTop: 8, maxWidth: 400, lineHeight: 1.65 }}>
          {tableSource === "image"
            ? "อัปโหลดรูปตาราง draft ที่ด้านซ้าย หรือลากรูปมาวางที่ dropzone เพื่อให้ AI อ่านอัตโนมัติ"
            : "ปรับ filter หรือเปลี่ยน source กลับเป็น Image OCR เพื่ออัปโหลดรูป"}
        </div>
      </div>
      {tableSource === "image" && (
        <button onClick={() => fileInputRef.current?.click()} disabled={ocrBusy} className="draft-primary-btn" style={{ ...primaryBtn(ocrBusy), padding: "11px 22px", fontSize: 13 }}>
          📤 อัปโหลดรูป
        </button>
      )}
    </div>
  );

  return (
    <div style={shell}>
      <style>{`
        @keyframes draftRecapSpin { to { transform: rotate(360deg); } }
        @keyframes draftRecapPulse { 0%,100% { opacity: 1; } 50% { opacity: .55; } }
        @keyframes draftRecapFadeUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .draft-input::placeholder { color: rgba(255,255,255,.26); }
        .draft-input:focus { border-color: ${recapTheme.borderStrong} !important; background: rgba(255,255,255,.05) !important; }
        .draft-ghost-btn:hover:not(:disabled) { background: rgba(255,255,255,.09) !important; border-color: rgba(255,255,255,.14) !important; transform: translateY(-1px); }
        .draft-primary-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(255,16,38,.36) !important; }
        .draft-row:hover td { background-image: linear-gradient(0deg, rgba(255,16,38,.06), rgba(255,16,38,.06)) !important; }
        .draft-export-btn:hover:not(:disabled) { color: ${recapTheme.primaryLight} !important; border-color: ${recapTheme.border} !important; background: rgba(255,16,38,.08) !important; }
        .draft-segment:hover:not([data-active="true"]) { color: ${recapTheme.text} !important; background: rgba(255,255,255,.04) !important; }
        .draft-card { animation: draftRecapFadeUp .25s ease; }
      `}</style>

      <header style={stickyBar}>
        <div style={{ maxWidth: 2400, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", inset: -6, borderRadius: 16, background: `radial-gradient(circle, ${recapTheme.primaryGlow} 0%, transparent 70%)`, filter: "blur(6px)", pointerEvents: "none" }} />
              <div style={{ position: "relative", width: 40, height: 40, borderRadius: 12, background: `linear-gradient(135deg, ${recapTheme.primaryLight} 0%, ${recapTheme.primary} 45%, #7a000f 100%)`, display: "grid", placeItems: "center", fontWeight: 900, fontSize: 18, color: "#fff", boxShadow: "0 8px 22px rgba(255,16,38,.32), inset 0 1px 0 rgba(255,255,255,.25)", letterSpacing: "-.02em" }}>
                D
              </div>
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: 21,
                fontWeight: 900,
                letterSpacing: "-.02em",
                lineHeight: 1.1,
                background: `linear-gradient(135deg, #ffffff 0%, ${recapTheme.primaryLight} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>Draft Recap</h1>
              <div style={{ fontSize: 11, color: recapTheme.textDim, marginTop: 3, letterSpacing: ".01em", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: recapTheme.primary, boxShadow: `0 0 8px ${recapTheme.primary}` }} />
                OCR ตาราง draft • ลากรูปวางได้เลย
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={rowsBadge}>
              <span style={{ opacity: .6, marginRight: 4 }}>▦</span>
              {shownRows.length} rows
            </div>
            <div style={separator} />
            <button onClick={exportPng} disabled={!shownRows.length} className="draft-primary-btn" style={primaryBtn(!shownRows.length)}>📷 Export รูป (PNG)</button>
            <button onClick={exportCsv} disabled={!shownRows.length} className="draft-export-btn" style={ghostBtn(!shownRows.length)}>CSV</button>
            <button onClick={exportXlsx} disabled={!shownRows.length} className="draft-export-btn" style={ghostBtn(!shownRows.length)}>XLSX</button>
            <div style={separator} />
            <button onClick={() => navigate("/dashboard")} className="draft-ghost-btn" style={ghostBtn(false)}>Dashboard</button>
            <button onClick={() => navigate("/draft")} className="draft-ghost-btn" style={ghostBtn(false)}>Draft</button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 2400, margin: "0 auto", padding: "20px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "300px minmax(0, 1fr)", gap: 16, alignItems: "start" }}>

          <aside style={{ display: "flex", flexDirection: "column", gap: 12, position: "sticky", top: 86 }}>
            <div style={card} className="draft-card">
              <div style={cardLabel}><span style={cardLabelDot} />Source</div>
              <div style={segmented}>
                <button onClick={() => chooseSource("image")} className="draft-segment" data-active={tableSource === "image"} style={segmentBtn(tableSource === "image")}>Image OCR</button>
                <button onClick={() => chooseSource("xlsx")} className="draft-segment" data-active={tableSource === "xlsx"} style={segmentBtn(tableSource === "xlsx")}>XLSX</button>
                {excelFile && (
                  <button
                    onClick={() => chooseSource("specter")}
                    className="draft-segment"
                    data-active={tableSource === "specter"}
                    style={segmentBtn(tableSource === "specter")}
                    title={`ชีท Specter_Scrim — ดราฟของทีมอื่นที่เราส่องมา (ไม่มีเกมของ ${TEAM_SHORT})`}
                  >
                    ที่ส่องมา
                  </button>
                )}
              </div>
              {tableSource === "specter" && (
                <div style={{ ...inputLabel, marginTop: 10, lineHeight: 1.6 }}>
                  {specterLoading
                    ? "กำลังอ่านชีท Specter_Scrim…"
                    : `ดราฟที่ส่องมา ${scoutedRows.length} เกม — พิมพ์ชื่อทีมในช่องค้นหาเพื่อให้ทีมนั้นอยู่คอลัมน์ TEAM`}
                </div>
              )}
              {tableSource === "image" && (
                <>
                  <div style={{ ...inputLabel, marginTop: 12 }}>Engine</div>
                  <div style={segmented}>
                    <button onClick={() => setOcrEngine("api")} className="draft-segment" data-active={ocrEngine === "api"} style={segmentBtn(ocrEngine === "api")}>API (OpenAI)</button>
                    <button onClick={() => setOcrEngine("local")} className="draft-segment" data-active={ocrEngine === "local"} style={segmentBtn(ocrEngine === "local")}>Local (Tesseract)</button>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 10, color: recapTheme.textMuted, lineHeight: 1.5 }}>
                    {ocrEngine === "local"
                      ? "ฟรี • รันใน browser • ครั้งแรกโหลด ~10MB"
                      : "ส่งรูปไป OpenAI Vision • คุณภาพดีกว่า • เสียค่า API"}
                  </div>
                </>
              )}
            </div>

            <div style={card} className="draft-card">
              <div style={cardLabel}><span style={cardLabelDot} />Filters</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <div style={inputLabel}>Opponent</div>
                  <input value={teamSearch} onChange={(e) => setTeamSearch(e.target.value)} placeholder={`เช่น ${TEAM_SHORT}`} className="draft-input" style={inputBase} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={inputLabel}>From</div>
                    <input value={dateFrom} onChange={(e) => setDateFrom(ymd(e.target.value))} placeholder="20260101" className="draft-input" style={inputBase} />
                  </div>
                  <div>
                    <div style={inputLabel}>To</div>
                    <input value={dateTo} onChange={(e) => setDateTo(ymd(e.target.value))} placeholder="20261231" className="draft-input" style={inputBase} />
                  </div>
                </div>
              </div>
            </div>

            <div style={card} className="draft-card">
              <div style={cardLabel}><span style={cardLabelDot} />Upload</div>
              {!uploadedFile ? (
                <div
                  style={dropzone(dragActive, ocrBusy)}
                  onClick={() => !ocrBusy && fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {ocrBusy ? (
                    <>
                      <div style={{ display: "inline-block", width: 28, height: 28, border: "3px solid rgba(255,16,38,.25)", borderTop: "3px solid #ff1026", borderRadius: "50%", animation: "draftRecapSpin .7s linear infinite", marginBottom: 10 }} />
                      <div style={{ fontSize: 13, fontWeight: 800 }}>กำลังอ่านรูป...</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{dragActive ? "✨" : "📤"}</div>
                      <div style={{ fontSize: 13, fontWeight: 800 }}>{dragActive ? "ปล่อยรูปได้เลย" : "ลากรูปมาที่นี่"}</div>
                      <div style={{ fontSize: 11, color: recapTheme.textMuted, marginTop: 4 }}>หรือคลิกเพื่อเลือกไฟล์</div>
                    </>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 10, padding: 10, display: "flex", gap: 10, alignItems: "center" }}>
                    <img src={previewUrl} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", background: "rgba(255,255,255,.04)", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{uploadedFile.name}</div>
                      <div style={{ fontSize: 10, color: recapTheme.textMuted, marginTop: 2 }}>{(uploadedFile.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => runImageParse(uploadedFile)} disabled={ocrBusy} title="อ่านซ้ำ" style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.04)", color: recapTheme.text, cursor: ocrBusy ? "wait" : "pointer", fontSize: 13, display: "grid", placeItems: "center", lineHeight: 1 }}>↻</button>
                      <button onClick={clearImageMode} title="ล้าง" style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid rgba(255,85,119,.20)", background: "rgba(255,85,119,.08)", color: "#ff9fb4", cursor: "pointer", fontSize: 15, display: "grid", placeItems: "center", lineHeight: 1 }}>×</button>
                    </div>
                  </div>
                  <button onClick={() => fileInputRef.current?.click()} disabled={ocrBusy} style={{ width: "100%", marginTop: 8, padding: "8px 12px", borderRadius: 10, border: "1px dashed rgba(255,255,255,.12)", background: "transparent", color: recapTheme.textDim, fontSize: 11, fontWeight: 700, cursor: ocrBusy ? "wait" : "pointer" }}>↑ อัปโหลดรูปอื่น</button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
              <button onClick={addImageRow} style={{ width: "100%", marginTop: 8, padding: "8px 12px", borderRadius: 10, border: "1px dashed rgba(255,255,255,.10)", background: "transparent", color: recapTheme.textDim, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+ เพิ่มแถวเอง</button>
            </div>

            {ocrStatus && (
              <div className="draft-card" style={{
                background: ocrBusy
                  ? "linear-gradient(135deg, rgba(255,16,38,.14) 0%, rgba(255,16,38,.04) 100%)"
                  : "linear-gradient(180deg, rgba(20,20,20,.7) 0%, rgba(12,12,12,.7) 100%)",
                border: `1px solid ${ocrBusy ? recapTheme.border : "rgba(255,255,255,.06)"}`,
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 11,
                color: ocrBusy ? recapTheme.primaryLight : recapTheme.textDim,
                lineHeight: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                boxShadow: ocrBusy ? `0 0 0 1px ${recapTheme.border}, 0 0 18px rgba(255,16,38,.10)` : "inset 0 1px 0 rgba(255,255,255,.03)",
              }}>
                {ocrBusy ? (
                  <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid rgba(255,16,38,.3)", borderTop: "2px solid #ff1026", borderRadius: "50%", animation: "draftRecapSpin .6s linear infinite", flexShrink: 0, marginTop: 2 }} />
                ) : (
                  <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: recapTheme.success, boxShadow: `0 0 6px ${recapTheme.success}`, flexShrink: 0, marginTop: 5 }} />
                )}
                <div style={{ fontWeight: ocrBusy ? 700 : 600 }}>{ocrStatus}</div>
              </div>
            )}
          </aside>

          <main style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
            <div ref={exportImageRef} style={tableCard}>
              <div style={{
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                borderBottom: "1px solid rgba(255,255,255,.06)",
                flexWrap: "wrap",
                background: "linear-gradient(180deg, rgba(255,16,38,.04) 0%, transparent 100%)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 4, height: 32, borderRadius: 2, background: `linear-gradient(180deg, ${recapTheme.primaryLight} 0%, ${recapTheme.primary} 100%)`, boxShadow: `0 0 10px ${recapTheme.primaryGlow}` }} />
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 17, letterSpacing: "-.01em" }}>Draft Table</div>
                    <div style={{ color: recapTheme.textDim, fontSize: 12, marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
                      {tableSource === "image" ? (
                        <>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: recapTheme.teamSoft, border: `1px solid ${recapTheme.team}` }} />
                            TEAM
                          </span>
                          <span style={{ opacity: .5 }}>·</span>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: recapTheme.oppSoft, border: `1px solid ${recapTheme.opp}` }} />
                            OPP
                          </span>
                          <span style={{ opacity: .5 }}>·</span>
                          <span>คลิก cell เพื่อแก้</span>
                        </>
                      ) : "ข้อมูลจาก XLSX (อ่านอย่างเดียว)"}
                    </div>
                  </div>
                </div>
                <div style={rowsBadge}>
                  <span style={{ opacity: .6, marginRight: 4 }}>▦</span>
                  {shownRows.length} rows
                </div>
              </div>

              {!shownRows.length ? renderEmptyState() : (
                <div ref={tableScrollRef} style={{ overflow: "auto", maxHeight: "calc(100vh - 220px)" }}>
                  <table style={{ width: "100%", minWidth: 2440, borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
                    <colgroup>
                      {ROW_COLUMNS.map((field) => (
                        <col key={field} style={{ width: COLUMN_WIDTHS[field] || 96 }} />
                      ))}
                    </colgroup>
                    <thead>
                      <tr>
                        {COLUMN_GROUPS.map((group, idx) => (
                          <th
                            key={`${group.label}-${idx}`}
                            colSpan={group.span}
                            style={{
                              ...groupTh,
                              background: `linear-gradient(180deg, rgba(255,255,255,.04) 0%, ${group.accent} 100%)`,
                              color: group.color,
                              borderLeft: idx === 0 ? "none" : `1px solid rgba(255,255,255,.08)`,
                            }}
                          >
                            {group.label}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        {ROW_COLUMNS.map((field) => (
                          <th key={field} style={{ ...th, background: getColumnColors(field).head, color: field === "bruWin" ? recapTheme.success : recapTheme.text }}>
                            {renderHeaderLabel(field)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shownRows.map((row, index) => {
                        const rowId = row?._id || `row-${index}`;
                        const isEditable = row?._source === "image";
                        const nextRow = shownRows[index + 1];
                        const showSeriesDivider = isSeriesBoundary(row, nextRow);
                        return (
                          <React.Fragment key={rowId}>
                            <tr className="draft-row">
                              {ROW_COLUMNS.map((field) => {
                                const active = editingCell?.rowId === rowId && editingCell?.field === field;
                                const colors = getColumnColors(field);
                                const isHero = HERO_COLUMNS.includes(field);
                                const isWin = field === "bruWin";
                                const value = row?.[field] ?? "";

                                if (!isEditable) {
                                  return (
                                    <td
                                      key={field}
                                      style={{
                                        ...td,
                                        background: isWin
                                          ? (Number(value) === 1 ? recapTheme.successSoft : recapTheme.dangerSoft)
                                          : colors.body,
                                        fontWeight: isHero || isWin ? 700 : 500,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {isHero ? <HeroCell value={value} /> : isWin ? <WinCell value={value} /> : String(value ?? "")}
                                    </td>
                                  );
                                }

                                return (
                                  <td
                                    key={field}
                                    style={{
                                      ...td,
                                      background: isWin
                                        ? (Number(value) === 1 ? recapTheme.successSoft : recapTheme.dangerSoft)
                                        : colors.body,
                                      cursor: "pointer",
                                      fontWeight: isHero || isWin ? 700 : 500,
                                      minWidth: COLUMN_WIDTHS[field] || (isHero ? 92 : 70),
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                    onClick={() => setEditingCell({ rowId, field })}
                                  >
                                    {active ? (
                                      isWin ? (
                                        <select
                                          autoFocus
                                          value={String(Number(value) === 1 ? 1 : 0)}
                                          onBlur={() => setEditingCell(null)}
                                          onChange={(e) => updateImageCell(rowId, field, e.target.value)}
                                          style={{ width: 72, border: `1px solid ${recapTheme.borderStrong}`, borderRadius: 8, padding: "4px 6px", background: recapTheme.bgDark, color: recapTheme.text }}
                                        >
                                          <option value="0">LOSS</option>
                                          <option value="1">WIN</option>
                                        </select>
                                      ) : (
                                        <input
                                          autoFocus
                                          value={String(value)}
                                          onBlur={() => setEditingCell(null)}
                                          onChange={(e) => updateImageCell(rowId, field, e.target.value)}
                                          style={{ width: isHero ? 100 : 78, border: `1px solid ${recapTheme.borderStrong}`, borderRadius: 8, padding: "4px 6px", background: recapTheme.bgDark, color: recapTheme.text }}
                                        />
                                      )
                                    ) : (
                                      isHero ? <HeroCell value={value} /> : isWin ? <WinCell value={value} /> : String(value ?? "")
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                            {showSeriesDivider ? (
                              <tr>
                                <td colSpan={ROW_COLUMNS.length} style={{ padding: "10px 16px", background: "rgba(0,0,0,.18)", borderBottom: "1px solid rgba(255,255,255,.04)" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
                                    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent 0%, rgba(255,16,38,.55) 100%)" }} />
                                    <div style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: ".28em", color: recapTheme.primaryLight, textTransform: "uppercase", padding: "4px 12px", borderRadius: 999, background: "rgba(255,16,38,.10)", border: `1px solid ${recapTheme.border}`, whiteSpace: "nowrap" }}>
                                      End of Series
                                    </div>
                                    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(255,16,38,.55) 0%, transparent 100%)" }} />
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {previewUrl && (
              <details style={{
                background: "linear-gradient(180deg, rgba(20,20,20,.78) 0%, rgba(12,12,12,.78) 100%)",
                border: "1px solid rgba(255,255,255,.06)",
                borderRadius: 14,
                overflow: "hidden",
                boxShadow: "0 6px 20px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.04)",
              }}>
                <summary style={{
                  cursor: "pointer",
                  padding: "12px 18px",
                  fontSize: 13,
                  fontWeight: 800,
                  color: recapTheme.text,
                  listStyle: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  userSelect: "none",
                }}>
                  <span style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(255,16,38,.10)", border: `1px solid ${recapTheme.border}`, display: "grid", placeItems: "center", fontSize: 13 }}>🖼️</span>
                  <span>ดูรูปต้นฉบับที่อัปโหลด</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, color: recapTheme.textMuted, fontWeight: 600, padding: "3px 8px", borderRadius: 6, background: "rgba(255,255,255,.04)" }}>{uploadedFile?.name}</span>
                </summary>
                <div style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,.05)", background: "rgba(0,0,0,.3)", overflow: "auto" }}>
                  <img src={previewUrl} alt="uploaded" style={{ width: "100%", minWidth: 800, borderRadius: 8, display: "block" }} />
                </div>
              </details>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
